/**
 * Department OKR Node Component
 * Custom ReactFlow node for Department-level OKRs
 */

import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Landmark, ChevronDown } from 'lucide-react';
import { OKR } from '../../../types/okr';
import { keyResultProgressPercent } from '../../../utils/progress';

interface DepartmentOKRNodeData {
  okr: OKR;
  childCount?: number;
  selected?: boolean;
}

export const DepartmentOKRNode: React.FC<NodeProps<DepartmentOKRNodeData>> = ({ data }) => {
  const { okr, childCount = 0, selected = false } = data;
  const progress = okr.progress || 0;
  const keyResults = okr.keyResults || [];
  const displayKRs = keyResults.slice(0, 3);
  const remainingKRs = keyResults.length - displayKRs.length;

  const truncateTitle = (title: string, maxLength: number = 35) => {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength) + '...';
  };

  const deptName = okr.owner.department || 'Department';

  return (
    <div className={`department-okr-node ${selected ? 'ring-4 ring-yellow-400 ring-offset-2 rounded-lg' : ''}`}>
      {/* Top handle for incoming connections */}
      <Handle type="target" position={Position.Top} className="!bg-amber-600" />

      {/* Node content */}
      <div className={`w-[260px] bg-gradient-to-br from-amber-500 to-amber-600 border-2 ${selected ? 'border-yellow-400' : 'border-amber-700'} rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer`}>
        {/* Header */}
        <div className="px-3.5 py-2.5 border-b border-amber-400/30">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="flex-shrink-0 w-7 h-7 bg-white/20 rounded-lg flex items-center justify-center">
              <Landmark className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-amber-100 uppercase tracking-wide">
                {deptName}
              </div>
            </div>
          </div>
          <h3 className="text-white font-semibold text-sm leading-tight">
            {truncateTitle(okr.title)}
          </h3>
        </div>

        {/* Progress bar */}
        <div className="px-3.5 py-2.5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-amber-100">Progress</span>
            <span className="text-xs font-bold text-white">{progress}%</span>
          </div>
          <div className="w-full bg-amber-800/40 rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-white to-amber-100 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Key Results */}
        {displayKRs.length > 0 && (
          <div className="px-3.5 py-2 border-t border-amber-400/20">
            <p className="text-xs font-medium text-amber-100 mb-1.5">Key Results:</p>
            <div className="space-y-1.5">
              {displayKRs.map((kr) => {
                const krProgress = keyResultProgressPercent(kr);
                return (
                  <div key={kr.id} className="text-xs">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-amber-50 truncate pr-2">{truncateTitle(kr.title, 22)}</span>
                      <span className="text-white font-medium text-[10px]">{krProgress}%</span>
                    </div>
                    <div className="w-full bg-amber-800/40 rounded-full h-1 overflow-hidden">
                      <div
                        className="h-full bg-amber-200 rounded-full transition-all duration-300"
                        style={{ width: `${krProgress}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              {remainingKRs > 0 && (
                <p className="text-[10px] text-amber-200 italic text-center mt-1">
                  +{remainingKRs} more
                </p>
              )}
            </div>
          </div>
        )}

        {/* Footer - Child count */}
        {childCount > 0 && (
          <div className="px-3.5 py-2 bg-amber-700/30 border-t border-amber-400/20 rounded-b-lg">
            <div className="flex items-center justify-center gap-1 text-white">
              <ChevronDown className="w-3.5 h-3.5" />
              <span className="text-xs font-medium">
                {childCount} {childCount === 1 ? 'team' : 'teams'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Bottom handle for outgoing connections */}
      <Handle type="source" position={Position.Bottom} className="!bg-amber-600" />
    </div>
  );
};
